import { parseServerTimestamp } from "./traceUtils.js";

const CONTROL_NAMES = {
  permission_rule: "a permission rule",
  managed_policy: "managed policy",
  permission_mode: "the permission mode",
  hook: "a hook",
  auto_mode_block: "auto mode",
  auto_mode_no_verdict: "auto mode, which reached no verdict",
  safety_check: "a built-in safety check",
  static_analysis: "command analysis",
  other: "a safety check",
};

const RULE_SOURCE_NAMES = {
  policySettings: "managed policy",
  userSettings: "user settings",
  projectSettings: "project settings",
  localSettings: "local project settings",
  cliArg: "a command-line flag",
  flagSettings: "command-line settings",
  session: "this session",
};

const HOOK_NAMES = {
  pre_tool_use: "PreToolUse",
  post_tool_use: "PostToolUse",
  post_tool_use_failure: "PostToolUseFailure",
  stop: "Stop",
  subagent_stop: "SubagentStop",
};

const SANDBOX_REFUSALS = { sandbox_network: "network connection", sandbox_other: "request" };

const words = (value) => String(value).replace(/[_:]+/g, " ").trim();
const counted = (count, noun) => (count ? `${count} ${noun}${count === 1 ? "" : "s"}` : `a ${noun}`);

function safetyAction({ category, decision, hookEvent, sandboxViolationCount }) {
  if (SANDBOX_REFUSALS[category]) return `Sandbox refused ${counted(sandboxViolationCount, SANDBOX_REFUSALS[category])}`;
  if (category === "sandbox_override" && decision === "accept") return "Approved to run outside the sandbox";
  if (category === "sandbox_override" && decision === "reject") return "Declined to run outside the sandbox";
  if (category === "user_rejected" && decision === "reject") return "Declined at a permission prompt";
  if (category === "hook_review" && decision === "sent_back") {
    const hook = HOOK_NAMES[hookEvent] ? `A ${HOOK_NAMES[hookEvent]} hook` : "A hook";
    return hookEvent === "stop" || hookEvent === "subagent_stop" ? `${hook} blocked the end of the turn` : `${hook} blocked the result`;
  }
  const control = category === "hook" && HOOK_NAMES[hookEvent] ? `a ${HOOK_NAMES[hookEvent]} hook` : CONTROL_NAMES[category];
  if (control && decision === "reject") return `Blocked by ${control}`;
  if (control && decision === "accept") return `Approved at a prompt from ${control}`;
  return `Safety event: ${words(category)}${decision ? ` (${words(decision)})` : ""}`;
}

export function toolSafetyLabel(safety) {
  if (!safety?.category) return "";
  const parts = [`${safetyAction(safety)}.`];
  if (safety.ruleSource && !SANDBOX_REFUSALS[safety.category]) parts.push(`Rule from ${RULE_SOURCE_NAMES[safety.ruleSource] || words(safety.ruleSource)}.`);
  if (!SANDBOX_REFUSALS[safety.category] && safety.sandboxViolationCount > 0) {
    const n = safety.sandboxViolationCount;
    parts.push(`The sandbox also refused ${n === 1 ? "a connection or request" : `${n} connections or requests`}.`);
  }
  return parts.join(" ");
}

export function toolSafetyBlocked(safety) {
  return safety?.decision === "reject" || safety?.decision === "sent_back"
    || Boolean(SANDBOX_REFUSALS[safety?.category]) || safety?.sandboxViolationCount > 0;
}

function shapeToolSafety(row) {
  const count = row.sandbox_violation_count == null || row.sandbox_violation_count === "" ? null : Number(row.sandbox_violation_count);
  return {
    eventId: row.event_id ? String(row.event_id) : "",
    category: String(row.category),
    decision: row.decision ? String(row.decision) : "",
    ruleSource: row.rule_source ? String(row.rule_source) : "",
    reasonCode: row.reason_code ? String(row.reason_code) : "",
    decisionSource: row.decision_source ? String(row.decision_source) : "",
    hookEvent: row.hook_event ? String(row.hook_event) : "",
    sandboxViolationCount: Number.isFinite(count) ? count : null,
  };
}

export function toolSafetyByUseId(rows) {
  const byUseId = new Map();
  const seen = new Set();
  for (const row of rows || []) {
    const useId = row.tool_use_id ? String(row.tool_use_id) : "";
    if (!useId || !row.category) continue;
    const safety = shapeToolSafety(row);
    if (safety.eventId && seen.has(safety.eventId)) continue;
    if (safety.eventId) seen.add(safety.eventId);
    byUseId.set(useId, [...(byUseId.get(useId) || []), safety]);
  }
  return byUseId;
}

// Typed trace-tool activity rows retain source truncation flags.
export function shapeToolExecutions(rows) {
  return (rows || []).map((row) => ({
    id: String(row.tool_event_id), name: row.name || "tool",
    // Empty for a provider that reported no call id; readers key on it to date
    // a captured tool_use block, and an empty key must never match one.
    toolUseId: row.tool_use_id ? String(row.tool_use_id) : "",
    timestamp: row.tool_time ? parseServerTimestamp(row.tool_time) : null,
    isError: row.isError === true, input: row.input ?? "", result: row.result ?? "",
    inputTruncated: row.inputTruncated === true, resultTruncated: row.resultTruncated === true,
  }));
}
